/**
 * The bulk action bar (spec M1-B3 §8.4), measured on `[ui:library/item-03-multi-select-bar.png]`
 * and `[…json]`: white, `border-radius: 8px`, `box-shadow: rgba(0,0,0,.3) 0 15px 50px`,
 * `padding: 10px 16px`, **66 px** tall, 736 px wide, centred over the grid at the bottom of the
 * viewport, class `batch-actions-menu-wrapper-v2`.
 *
 * Contents, left to right, exactly: a count badge + `Items selected`, **Duplicate · Export ·
 * Archive · Delete · Convert · Move to**, then `✕`. `Sidekick` and `Apps` are monday-only and
 * **not rendered**. There is no "Apply to each" button in this build — do not build one (Q161).
 *
 * **B10**: `Export`, `Convert` and `Move to` had no handler at all. `Convert` and `Move to`
 * open an anchored menu (the same targets the row menu's `Convert to subitem` / `Move to ▸`
 * offer) and `Export` writes the selection as CSV (`export-rows.ts`, a registered deviation:
 * monday's file is `.xlsx`, and no `export.items` procedure exists).
 */
import { Icon, icons } from '@elizmonday/ui';
import { useState } from 'react';
import { useTable, useTableActions, useTableContext } from './context';
import { EMPTY_ITEM_SELECTION } from './selection';
import { Menu, type MenuEntry } from './menus/Menu';
import { MoveItemsDialog } from './menus/MoveItemsDialog';
import { downloadCsv, safeFilename, toCsv } from './menus/export-rows';

type BulkMenu = { kind: 'move' | 'convert'; x: number; y: number } | null;

export function MultiSelectBar() {
  const selection = useTable((state) => state.itemSelection);
  const payload = useTable((state) => state.payload);
  const actions = useTableActions();
  const { source } = useTableContext();
  const [menu, setMenu] = useState<BulkMenu>(null);
  const [movingToBoard, setMovingToBoard] = useState(false);

  const count = selection.ids.length;
  if (count === 0) return null;

  const terminology = count === 1 ? payload?.board.itemTerminology : payload?.board.itemTerminologyPlural;
  const selected = new Set(selection.ids.map(String));
  const rows = (payload?.groups ?? []).flatMap((group) =>
    group.page.items.filter((item) => selected.has(String(item.id))),
  );

  const exportSelection = () => {
    downloadCsv(
      safeFilename(payload?.board.name ?? 'Board', `${String(count)} selected`),
      toCsv(payload?.columns ?? [], rows, payload?.board.itemTerminology ?? 'Name'),
    );
  };

  const convertTo = async (parentItemId: (typeof selection.ids)[number]) => {
    setMenu(null);
    await source.convertItemsToSubitems({
      boardId: actions.boardId as never,
      itemIds: selection.ids,
      parentItemId,
    });
    actions.setItemSelection(EMPTY_ITEM_SELECTION);
    await actions.load();
  };

  const moveEntries: MenuEntry[] = [
    ...(payload?.groups ?? []).map((group) => ({
      id: `group-${String(group.id)}`,
      label: group.title,
      onSelect: () => {
        setMenu(null);
        void actions.moveItems(selection.ids, group.id);
      },
    })),
    { id: 'd1', label: '', divider: true },
    {
      id: 'board',
      label: 'Another board…',
      onSelect: () => {
        setMenu(null);
        setMovingToBoard(true);
      },
    },
  ];

  const convertEntries: MenuEntry[] = (payload?.groups ?? [])
    .flatMap((group) => group.page.items)
    .filter((item) => !selected.has(String(item.id)))
    .slice(0, 30)
    .map((item) => ({
      id: `parent-${String(item.id)}`,
      label: item.name,
      onSelect: () => void convertTo(item.id),
    }));

  const action = (
    id: string,
    label: string,
    icon: keyof typeof icons,
    run?: (event: React.MouseEvent<HTMLButtonElement>) => void,
  ) => (
    <button
      type="button"
      className="ez-bulk__action"
      data-testid={`bulk-${id}`}
      disabled={!run}
      onClick={run}
    >
      <Icon icon={icons[icon]} size={18} ignoreFocusStyle />
      <span>{label}</span>
    </button>
  );

  const openMenu = (kind: 'move' | 'convert') => (event: React.MouseEvent<HTMLButtonElement>) => {
    const box = event.currentTarget.getBoundingClientRect();
    setMenu({ kind, x: box.left, y: box.top - 8 });
  };

  return (
    <div className="ez-bulk" role="region" aria-label="Bulk actions" data-testid="bulk-bar">
      <span className="ez-bulk__count" data-testid="bulk-count">
        {count}
      </span>
      <span className="ez-bulk__label">{`${terminology ?? 'items'} selected`}</span>

      {action('duplicate', 'Duplicate', 'duplicate', () => void actions.duplicateItems(selection.ids))}
      {action('export', 'Export', 'download', exportSelection)}
      {action('archive', 'Archive', 'archive', () => void actions.archiveItems(selection.ids))}
      {action('delete', 'Delete', 'delete', () => void actions.deleteItems(selection.ids))}
      {action('convert', 'Convert', 'convertToSubitem', openMenu('convert'))}
      {action('move', 'Move to', 'moveArrowRight', openMenu('move'))}

      <button
        type="button"
        className="ez-bulk__close"
        aria-label="Clear selection"
        onClick={() => actions.setItemSelection(EMPTY_ITEM_SELECTION)}
      >
        <Icon icon={icons.close} size={16} ignoreFocusStyle />
      </button>

      {menu ? (
        <Menu
          entries={menu.kind === 'move' ? moveEntries : convertEntries}
          x={menu.x}
          y={menu.y}
          width={240}
          label={menu.kind === 'move' ? 'Move to' : 'Convert to subitem of'}
          onClose={() => setMenu(null)}
        />
      ) : null}

      {movingToBoard ? (
        <MoveItemsDialog
          itemIds={selection.ids.map(String)}
          onClose={() => {
            setMovingToBoard(false);
            actions.setItemSelection(EMPTY_ITEM_SELECTION);
          }}
        />
      ) : null}
    </div>
  );
}
